import { useState } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { mergeClsContainerElements } from '../../utils/diagnosticsExportStyles';

const ActionPlan = ({ deepCLSResults, multiPageResults, url }) => {
  const { t, language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(true);

  if (!deepCLSResults && !multiPageResults) return null;

  // Pushed page containers (body-wrapper & co.) are victims of an insertion,
  // not culprits — they must never drive a "main culprit" claim or a
  // "reserve space" task. Same attribution rule as the server + exports.
  const actionableClsElements = deepCLSResults?.elements?.length
    ? mergeClsContainerElements(deepCLSResults.elements).filter(el => !el.isContainer)
    : [];

  const findBiggestShifter = (elements) => {
    if (!elements || elements.length === 0) return null;
    return elements.reduce((max, el) => 
      (el.totalShift || 0) > (max.totalShift || 0) ? el : max, 
      elements[0]
    );
  };

  const getTopShifters = (elements, count = 3) => {
    if (!elements || elements.length === 0) return [];
    return [...elements]
      .sort((a, b) => (b.totalShift || 0) - (a.totalShift || 0))
      .slice(0, count);
  };

  const generateClientSummary = () => {
    const parts = [];
    
    if (deepCLSResults) {
      const cls = deepCLSResults.cls || 0;
      const status = cls <= 0.1 ? 'good' : cls <= 0.25 ? 'needsWork' : 'critical';
      
      if (status === 'critical') {
        parts.push(language === 'cs' 
          ? `Web má vážný problém se stabilitou layoutu (CLS: ${cls.toFixed(3)}). Obsah stránky "poskakuje" při načítání nebo scrollování, což frustruje uživatele a negativně ovlivňuje pozici ve vyhledávačích.`
          : `The website has a serious layout stability issue (CLS: ${cls.toFixed(3)}). Page content "jumps" during loading or scrolling, frustrating users and negatively affecting search rankings.`);
      } else if (status === 'needsWork') {
        parts.push(language === 'cs'
          ? `Web má mírné problémy se stabilitou layoutu (CLS: ${cls.toFixed(3)}). Doporučujeme optimalizaci pro lepší uživatelský zážitek.`
          : `The website has moderate layout stability issues (CLS: ${cls.toFixed(3)}). We recommend optimization for better user experience.`);
      } else {
        parts.push(language === 'cs'
          ? `Stabilita layoutu je v pořádku (CLS: ${cls.toFixed(3)}).`
          : `Layout stability is good (CLS: ${cls.toFixed(3)}).`);
      }
      
      const biggestShifter = findBiggestShifter(actionableClsElements);
      if (biggestShifter && biggestShifter.totalShift > 0.1 && cls > 0) {
        const selector = biggestShifter.selector || biggestShifter.tagName;
        const shortSelector = selector;
        // A percentage-of-total claim is only honest on apportioned payloads
        // (each source carries its OWN portion of every shift). Legacy
        // payloads credited the full entry value to every source, so the
        // ratio overstates — show the raw shift value instead.
        if (deepCLSResults.attributionModel === 'apportioned') {
          const percentage = Math.min(100, (biggestShifter.totalShift / cls * 100));
          parts.push(language === 'cs'
            ? `Hlavní viník: element "${shortSelector}" způsobuje ${percentage.toFixed(0)}% všech posunů.`
            : `Main culprit: element "${shortSelector}" causes ${percentage.toFixed(0)}% of all shifts.`);
        } else {
          parts.push(language === 'cs'
            ? `Největší zaznamenaný zdroj posunů: element "${shortSelector}" (posun ${biggestShifter.totalShift.toFixed(3)}; hodnota může zahrnovat posuny sdílené s dalšími zdroji).`
            : `Largest recorded shift source: element "${shortSelector}" (shift ${biggestShifter.totalShift.toFixed(3)}; the value may include shifts shared with other sources).`);
        }
      }
    }
    
    if (multiPageResults) {
      const { summary } = multiPageResults;
      const totalPages = summary?.totalPages || multiPageResults?.pages?.length || 0;
      const poorPages = summary?.poorPages || 0;
      const needsImprovementPages = summary?.needsImprovementPages || 0;
      
      if (totalPages > 0) {
        if (poorPages > 0) {
          parts.push(language === 'cs'
            ? `Z ${totalPages} testovaných stránek má ${poorPages} kritické problémy. Jde o systémovou chybu v šabloně webu.`
            : `Out of ${totalPages} tested pages, ${poorPages} have critical issues. This is a systemic template problem.`);
        } else if (needsImprovementPages > 0) {
          parts.push(language === 'cs'
            ? `Z ${totalPages} testovaných stránek ${needsImprovementPages} potřebuje zlepšení.`
            : `Out of ${totalPages} tested pages, ${needsImprovementPages} need improvement.`);
        } else {
          parts.push(language === 'cs'
            ? `Všech ${totalPages} testovaných stránek má dobré CLS skóre.`
            : `All ${totalPages} tested pages have good CLS scores.`);
        }
      }
    }
    
    if (parts.length === 0) {
      parts.push(language === 'cs'
        ? 'Analýza neprokázala kritické problémy se stabilitou layoutu.'
        : 'Analysis did not reveal critical layout stability issues.');
    }
    
    return parts.join('\n\n');
  };

  const generateDeveloperTasks = () => {
    const tasks = [];
    const addedCauses = new Set();
    
    if (actionableClsElements.length > 0) {
      const topShifters = getTopShifters(actionableClsElements, 5);
      
      topShifters.forEach(shifter => {
        const cause = shifter.cause || 'unknown';
        
        if (addedCauses.has(cause)) return;
        
        const isSignificant = shifter.totalShift > 0.05;
        if (!isSignificant) return;
        
        const priority = shifter.totalShift > 0.5 ? 'critical' : shifter.totalShift > 0.1 ? 'high' : 'medium';
        
        if (cause === 'dynamic-content' || cause === 'unknown') {
          addedCauses.add(cause);
          tasks.push({
            priority,
            title: language === 'cs' ? 'Opravit dynamický widget' : 'Fix dynamic widget',
            selector: shifter.selector,
            description: language === 'cs'
              ? `Widget "${shifter.selector}" nemá rezervované místo a způsobuje layout shift ${shifter.totalShift?.toFixed(3)}.`
              : `Widget "${shifter.selector}" has no reserved space and causes layout shift of ${shifter.totalShift?.toFixed(3)}.`,
            fix: language === 'cs'
              ? `1. Přidejte min-height nebo aspect-ratio kontejner\n2. Pokud jde o reklamu/embed, použijte placeholder\n3. Načítejte obsah lazy až pod foldem`
              : `1. Add min-height or aspect-ratio container\n2. If it's an ad/embed, use a placeholder\n3. Load content lazily below the fold`
          });
        }
        
        if (cause === 'iframe-embed' || cause === 'video-embed') {
          addedCauses.add(cause);
          tasks.push({
            priority,
            title: language === 'cs' ? 'Rezervovat místo pro embed' : 'Reserve space for embed',
            selector: shifter.selector,
            description: language === 'cs'
              ? `Vložený obsah (iframe/video) nemá definované rozměry.`
              : `Embedded content (iframe/video) has no defined dimensions.`,
            fix: language === 'cs'
              ? `1. Obalte element kontejnerem s aspect-ratio: 16/9\n2. Nebo přidejte width a height atributy\n3. Pro lazy-loading použijte loading="lazy"`
              : `1. Wrap element in container with aspect-ratio: 16/9\n2. Or add width and height attributes\n3. For lazy loading use loading="lazy"`
          });
        }
        
        if (cause === 'unsized-image' || cause === 'image-load') {
          addedCauses.add(cause);
          tasks.push({
            priority,
            title: language === 'cs' ? 'Přidat rozměry obrázkům' : 'Add image dimensions',
            selector: shifter.selector,
            description: language === 'cs'
              ? `Obrázky bez width/height atributů způsobují layout shift.`
              : `Images without width/height attributes cause layout shift.`,
            fix: language === 'cs'
              ? `1. Přidejte width a height atributy ke všem <img>\n2. Použijte aspect-ratio v CSS\n3. Pro responzivní obrázky: srcset + sizes`
              : `1. Add width and height attributes to all <img>\n2. Use aspect-ratio in CSS\n3. For responsive images: srcset + sizes`
          });
        }
        
        if (cause === 'font-swap') {
          addedCauses.add(cause);
          tasks.push({
            priority,
            title: language === 'cs' ? 'Optimalizovat webfonty' : 'Optimize web fonts',
            selector: shifter.selector,
            description: language === 'cs'
              ? `Webfonty způsobují blikání textu (FOUT).`
              : `Web fonts cause text flashing (FOUT).`,
            fix: language === 'cs'
              ? `1. Přidejte font-display: swap do @font-face\n2. Preloadujte kritické fonty pomocí <link rel="preload">\n3. Zvažte použití size-adjust pro fallback fonty`
              : `1. Add font-display: swap to @font-face\n2. Preload critical fonts with <link rel="preload">\n3. Consider using size-adjust for fallback fonts`
          });
        }
        
        if (cause === 'ad-injection') {
          addedCauses.add(cause);
          tasks.push({
            priority,
            title: language === 'cs' ? 'Stabilizovat reklamní bloky' : 'Stabilize ad blocks',
            selector: shifter.selector,
            description: language === 'cs'
              ? `Reklamní skripty dynamicky vkládají obsah bez rezervovaného místa.`
              : `Ad scripts dynamically inject content without reserved space.`,
            fix: language === 'cs'
              ? `1. Rezervujte místo pomocí min-height na kontejneru\n2. Použijte aspect-ratio pro reklamní sloty\n3. Načítejte reklamy lazy až po hlavním obsahu`
              : `1. Reserve space using min-height on container\n2. Use aspect-ratio for ad slots\n3. Load ads lazily after main content`
          });
        }
      });
    }
    
    if (deepCLSResults?.phases?.scroll > 0.1) {
      tasks.push({
        priority: deepCLSResults.phases.scroll > 0.5 ? 'critical' : 'high',
        title: language === 'cs' ? 'Opravit shifty při scrollu' : 'Fix scroll-triggered shifts',
        description: language === 'cs'
          ? `${(deepCLSResults.phases.scroll).toFixed(3)} CLS vzniká až při scrollování - lazy-loaded obsah.`
          : `${(deepCLSResults.phases.scroll).toFixed(3)} CLS occurs during scrolling - lazy-loaded content.`,
        fix: language === 'cs'
          ? `1. Přidejte skeleton loadery pro lazy obsah\n2. Rezervujte místo pomocí min-height\n3. Použijte content-visibility s contain-intrinsic-size`
          : `1. Add skeleton loaders for lazy content\n2. Reserve space with min-height\n3. Use content-visibility with contain-intrinsic-size`
      });
    }
    
    if (tasks.length === 0 && deepCLSResults?.cls > 0.1) {
      tasks.push({
        priority: 'medium',
        title: language === 'cs' ? 'Obecná optimalizace CLS' : 'General CLS optimization',
        description: language === 'cs'
          ? `Nebyly detekovány konkrétní viníci, ale CLS je nad optimální hodnotou.`
          : `No specific culprits detected, but CLS is above optimal value.`,
        fix: language === 'cs'
          ? `1. Přidejte rozměry všem obrázkům a embedům\n2. Preloadujte kritické fonty\n3. Používejte skeleton loadery pro dynamický obsah`
          : `1. Add dimensions to all images and embeds\n2. Preload critical fonts\n3. Use skeleton loaders for dynamic content`
      });
    }
    
    tasks.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
    
    return tasks;
  };

  const generateImpactEstimate = () => {
    if (!deepCLSResults) return null;
    
    const cls = deepCLSResults.cls || 0;
    if (cls <= 0.1) return null;
    
    return {
      seo: language === 'cs'
        ? cls > 0.25 ? 'Vysoký dopad na SEO - Google penalizuje stránky s vysokým CLS' : 'Střední dopad na SEO'
        : cls > 0.25 ? 'High SEO impact - Google penalizes pages with high CLS' : 'Moderate SEO impact',
      ux: language === 'cs'
        ? cls > 0.25 ? 'Uživatelé mohou kliknout na špatné místo kvůli posunům' : 'Mírné narušení uživatelského zážitku'
        : cls > 0.25 ? 'Users may click wrong elements due to shifts' : 'Minor user experience disruption',
      conversion: language === 'cs'
        ? cls > 0.25 ? 'Může snížit konverze o 5-10%' : 'Minimální dopad na konverze'
        : cls > 0.25 ? 'May reduce conversions by 5-10%' : 'Minimal conversion impact'
    };
  };

  const copyToClipboard = () => {
    const tasks = generateDeveloperTasks();
    const summary = generateClientSummary();
    
    let text = `=== ${language === 'cs' ? 'AKČNÍ PLÁN - OPRAVA CLS' : 'ACTION PLAN - CLS FIX'} ===\n`;
    text += `URL: ${url}\n\n`;
    text += `--- ${language === 'cs' ? 'SHRNUTÍ PRO KLIENTA' : 'CLIENT SUMMARY'} ---\n`;
    text += summary + '\n\n';
    text += `--- ${language === 'cs' ? 'ÚKOLY PRO VÝVOJÁŘE' : 'DEVELOPER TASKS'} ---\n`;
    
    tasks.forEach((task, i) => {
      const priorityLabel = task.priority === 'critical' 
        ? (language === 'cs' ? '[KRITICKÉ]' : '[CRITICAL]')
        : task.priority === 'high' 
          ? (language === 'cs' ? '[VYSOKÁ]' : '[HIGH]')
          : (language === 'cs' ? '[STŘEDNÍ]' : '[MEDIUM]');
      
      text += `\n${i + 1}. ${priorityLabel} ${task.title}\n`;
      text += `   ${task.description}\n`;
      if (task.selector) text += `   Selector: ${task.selector}\n`;
      text += `   ${language === 'cs' ? 'Řešení' : 'Fix'}:\n`;
      task.fix.split('\n').forEach(line => {
        text += `   ${line}\n`;
      });
    });
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tasks = generateDeveloperTasks();
  const summary = generateClientSummary();
  const impact = generateImpactEstimate();

  const priorityColors = {
    critical: 'bg-red-500/20 border-red-500/40 text-red-400',
    high: 'bg-orange-500/20 border-orange-500/40 text-orange-400',
    medium: 'bg-amber-500/20 border-amber-500/40 text-amber-400'
  };

  const priorityLabels = {
    critical: language === 'cs' ? 'Kritické' : 'Critical',
    high: language === 'cs' ? 'Vysoká' : 'High',
    medium: language === 'cs' ? 'Střední' : 'Medium'
  };

  return (
    <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30">
      <div className="flex items-center justify-between mb-3">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
        >
          <Icons.FileText size={16} />
          <span className="font-medium text-sm">
            {language === 'cs' ? 'Akční plán' : 'Action Plan'}
          </span>
          <Icons.ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 transition-colors"
        >
          {copied ? (
            <>
              <Icons.Check size={12} />
              {language === 'cs' ? 'Zkopírováno!' : 'Copied!'}
            </>
          ) : (
            <>
              <Icons.Copy size={12} />
              {language === 'cs' ? 'Kopírovat' : 'Copy'}
            </>
          )}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-slate-900/50">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
              {language === 'cs' ? 'Shrnutí pro klienta' : 'Client Summary'}
            </div>
            <p className="text-sm text-slate-200 whitespace-pre-line">{summary}</p>
          </div>

          {impact && (
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-xs text-purple-400 mb-1">SEO</div>
                <div className="text-[10px] text-slate-300">{impact.seo}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-xs text-blue-400 mb-1">UX</div>
                <div className="text-[10px] text-slate-300">{impact.ux}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-xs text-green-400 mb-1">
                  {language === 'cs' ? 'Konverze' : 'Conversion'}
                </div>
                <div className="text-[10px] text-slate-300">{impact.conversion}</div>
              </div>
            </div>
          )}

          {tasks.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <Icons.CheckSquare size={12} />
                {language === 'cs' ? 'Úkoly pro vývojáře' : 'Developer Tasks'}
              </div>
              <div className="space-y-2">
                {tasks.map((task, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${priorityColors[task.priority]}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[task.priority]} border`}>
                        {priorityLabels[task.priority]}
                      </span>
                      <span className="text-sm font-medium text-white">{task.title}</span>
                    </div>
                    <p className="text-xs text-slate-300 mb-2">{task.description}</p>
                    {task.selector && (
                      <code className="text-[10px] text-cyan-300 bg-slate-900/50 px-2 py-1 rounded block mb-2 break-all">
                        {task.selector}
                      </code>
                    )}
                    <div className="text-xs text-slate-400">
                      <span className="font-medium">{language === 'cs' ? 'Řešení:' : 'Fix:'}</span>
                      <pre className="mt-1 whitespace-pre-wrap text-[10px] font-mono">{task.fix}</pre>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-700">
            {language === 'cs' 
              ? 'Tento akční plán byl vygenerován automaticky na základě diagnostiky. Pro detailní implementaci kontaktujte vývojáře.'
              : 'This action plan was generated automatically based on diagnostics. Contact a developer for detailed implementation.'}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionPlan;
